import { Dispatch, SetStateAction } from 'react';
import { Call, Device } from '@twilio/voice-sdk';
import { UseMutateFunction } from '@tanstack/react-query';
import { formatISO } from 'date-fns';

import { OngoingCall, RingingActionType, RingingStateType } from './types';
import { TwilioPostCallType } from '../../services/Twilio/types';
import queryClient from '../../services/Query';
import query from '../../constants/queries';

export const initDevice = (
    token: string,
    setDevice: Dispatch<SetStateAction<Device | undefined>>,
    handleIncomingCall: (call: Call) => void
) => {
    try {
        const newDevice = new Device(token, {
            // tokenRefreshMs: {to decide},
            logLevel: 1,
            closeProtection: true,
            enableImprovedSignalingErrorPrecision: true
        });

        if (newDevice) {
            setDevice(newDevice);
            newDevice.register();
            newDevice.on('registered', () => console.log('Device registered and ready to receive incoming calls.'));
            newDevice.on('error', (twilioError) => console.log('Device error', twilioError));
            newDevice.on('incoming', (call: Call) => {
                console.log('Incoming call has been recognized by the device');
                handleIncomingCall(call);
            });
        }
    } catch (error) {
        throw new Error('Twilio device issue');
    }
};

export const ringingReducer = (state: RingingStateType, action: RingingActionType): RingingStateType => {
    switch (action) {
        case 'showRingingDots':
            return { showRingingDots: true, startTimer: false, showDuration: false, showIncomingCall: false };
        case 'showIncomingCall':
            return { showRingingDots: false, startTimer: false, showDuration: false, showIncomingCall: true };
        case 'startTimer':
            return { showRingingDots: false, startTimer: true, showDuration: true, showIncomingCall: false };
        case 'showDuration':
            return { showRingingDots: false, startTimer: false, showDuration: true, showIncomingCall: false };
        default:
            return state;
    }
};

export const handleRinging = (
    call: Call,
    phone: string,
    dispatch: Dispatch<RingingActionType>,
    postCallData: UseMutateFunction<unknown, unknown, TwilioPostCallType, unknown>
) => {
    const callData = {
        twilio_call_sid: `${call?.parameters?.CallSid}`,
        to: `+1${phone}`,
        started_at: formatISO(new Date()),
        status: 'started'
    };
    console.log('Ringing. Waiting for the user to answer');

    dispatch('showRingingDots');
    postCallData(callData);
    return setTimeout(() => dispatch('startTimer'), 2000);
};

export const disconnectCall = (
    dispatch: Dispatch<RingingActionType>,
    call: Call,
    setOngoingCall: Dispatch<SetStateAction<OngoingCall | undefined>>,
    timerTimeoutId: NodeJS.Timeout | null = null
) => {
    console.log('The call has been disconnected');

    if (timerTimeoutId) {
        clearTimeout(timerTimeoutId);
        timerTimeoutId = null;
    }
    call?.removeAllListeners();
    setOngoingCall(undefined);
    // timeout to refresh the cache required to wait until backend process the call data
    setTimeout(async () => {
        await queryClient.invalidateQueries({ queryKey: [query.communication] });
        dispatch('showDuration');
    }, 1000);
};

export const displayFormattedTime = (seconds: number) => {
    const hNumber = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60)
        .toString()
        .padStart(2, '0');
    const s = Math.floor(seconds % 60)
        .toString()
        .padStart(2, '0');

    if (hNumber) {
        const h = hNumber.toString().padStart(2, '0');
        return h + ':' + m + ':' + s;
    }

    return m + ':' + s;
};

export const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];
